import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Shared button styles (replaces legacy `.btn-*` helpers).
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-ring',
        brand:
          'bg-primary-darkGreen text-white hover:bg-primary-darkGreen/90 focus-visible:ring-primary-darkGreen',
        destructive:
          'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
        success:
          'bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500',
        blue:
          'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring',
        /** Legacy `.btn-secondary` — white fill, navy border */
        secondary:
          'border border-secondary-800 bg-white text-secondary-800 hover:bg-secondary-50 focus-visible:ring-secondary-500',
        muted:
          'bg-gray-200 text-gray-700 hover:bg-gray-300 focus-visible:ring-gray-500',
        ghost:
          'bg-transparent text-gray-700 hover:bg-gray-50 focus-visible:ring-gray-500',
        link: 'text-primary underline-offset-4 hover:underline focus-visible:ring-ring',
      },
      size: {
        default: 'h-9 px-3 py-1.5',
        sm: 'h-8 rounded-md px-2.5 text-xs',
        lg: 'h-11 rounded-md px-6 text-base',
        xl: 'min-h-12 rounded-md px-6 py-3 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const Button = React.forwardRef(function Button (
  { className, variant, size, asChild = false, ...props },
  ref
) {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  )
})

export { Button, buttonVariants }
